import type { GameData } from "../game/model.ts";
import type { PlayerInput } from "../game/step.ts";
import { LocalPrediction } from "./predict.ts";
import type { BalanceSettings } from "../game/settings.ts";
import {
  pushSnapshot,
  sampleWorld,
  updateClockOffset,
  type InterpolatedWorld,
  type NetEnergy,
  type NetPlayer,
  type Snapshot,
} from "./snapshot.ts";

export type ConnectionStatus = "idle" | "connecting" | "joined" | "reconnecting" | "closed";

export type JoinedInfo = {
  you: string;
  room: string;
  width: number;
  height: number;
  settings: BalanceSettings;
  tickHz: number;
  snapshotHz: number;
  capacity: number;
};

/** The slice of WebSocket this client uses, so tests can supply a fake. */
export interface SocketLike {
  send(data: string): void;
  close(): void;
  onopen: ((event?: unknown) => void) | null;
  onclose: ((event?: unknown) => void) | null;
  onerror: ((event?: unknown) => void) | null;
  onmessage: ((event: { data: unknown }) => void) | null;
}

export type ArenaClientOptions = {
  url: string;
  name: string;
  /** Reconnect backoff ceiling. */
  maxBackoffMs?: number;
  /**
   * The client's clock. Defaults to `performance.now()`: it is monotonic, and it
   * shares an origin with requestAnimationFrame timestamps. Tests inject their own.
   */
  now?: () => number;
  socketFactory?: (url: string) => SocketLike;
  onStatus?: (status: ConnectionStatus) => void;
  onJoined?: (info: JoinedInfo) => void;
  onEvents?: (events: unknown[]) => void;
  /** A room the player picked; the arena falls back to its own choice if it cannot. */
  room?: string | null;
  onRanked?: (placement: { daily: number; weekly: number | null; allTime: number | null; unranked?: boolean }) => void;
  onMatchOver?: (board: unknown[]) => void;
};

/** Default endpoint: same origin as the page, upgraded to ws/wss. */
export function defaultArenaUrl(location: { protocol: string; host: string }) {
  const scheme = location.protocol === "https:" ? "wss:" : "ws:";
  return `${scheme}//${location.host}`;
}

/**
 * Talks to the arena. Owns the socket, the snapshot buffer and the input cadence;
 * knows nothing about rendering. Callers push intent with `setInput` and pull a
 * renderable world with `sample`.
 */
export class ArenaClient {
  private readonly options: Required<Pick<ArenaClientOptions, "maxBackoffMs" | "now">> & ArenaClientOptions;
  private socket: SocketLike | null = null;
  private buffer: Snapshot[] = [];
  /** The energy field, kept here because the server only sends what changed. */
  private energy = new Map<number, NetEnergy>();
  private energyList: NetEnergy[] = [];
  private energyDirty = false;
  private input: PlayerInput | null = null;
  private seq = 0;
  private stepAccumulator = 0;
  private partialStep = 0;
  /**
   * Gentle pacing so the server's input buffer holds a couple of frames. The server
   * must never skip a frame to drain a deep buffer — that breaks replay by exactly
   * one step — so the client produces slightly fewer or more instead, which costs
   * nothing because a frame it never creates is a frame it never predicted.
   */
  private rateScale = 1;
  private starvedSeen = 0;
  private backoffMs = 500;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private stopped = false;
  /** Client clock minus server clock, in ms. Null until the first snapshot lands. */
  private clockOffset: number | null = null;
  private serverMsPerTick = 1000 / 60;
  /** Local core simulated ahead of the stream, so your own input feels immediate. */
  readonly prediction = new LocalPrediction();
  /** The arena the prediction physics runs against; supplied by the render layer. */
  private predictionWorld: GameData | null = null;
  /** Held when a snapshot lands before the render layer has offered an arena. */
  private pendingAnchor: { player: NetPlayer; ack: number } | null = null;
  private pingSentAt = new Map<number, number>();
  private nextPingId = 1;
  private lastPingAt = 0;

  status: ConnectionStatus = "idle";
  you: string | null = null;
  joined: JoinedInfo | null = null;
  /**
   * True network round trip, from a ping the server answers immediately.
   */
  pingMs = 0;
  /**
   * How long from sending an input to seeing it acknowledged. Always larger than
   * `pingMs`: it also waits for the next server tick and the next snapshot
   * broadcast, so at 20Hz it carries up to 66ms of protocol delay on top of the
   * network. This is what actually governs how responsive the game feels.
   */
  latencyMs = 0;
  /**
   * The server's simulation clock in milliseconds, as best the client can estimate.
   * Anything the server also computes from that clock — orbit rotation, and so the
   * point a shot leaves from — has to be drawn against this, not the local one.
   */
  serverNow() {
    return this.options.now() - (this.clockOffset ?? 0);
  }

  /** Diagnostics: how far the prediction was off at the last reconcile. */
  get correction() { return this.prediction.lastCorrection; }
  get replayDepth() { return this.prediction.lastReplayDepth; }

  constructor(options: ArenaClientOptions) {
    this.options = {
      maxBackoffMs: 8000,
      now: () => performance.now(),
      ...options,
    };
  }

  private setStatus(status: ConnectionStatus) {
    if (this.status === status) return;
    this.status = status;
    this.options.onStatus?.(status);
  }

  connect() {
    if (this.socket || this.stopped) return;
    this.setStatus(this.you ? "reconnecting" : "connecting");

    const factory = this.options.socketFactory ?? ((url: string) => new WebSocket(url) as unknown as SocketLike);
    const socket = factory(this.options.url);
    this.socket = socket;

    socket.onopen = () => {
      this.backoffMs = 500;
      socket.send(JSON.stringify({ t: "join", name: this.options.name, room: this.options.room ?? undefined }));
    };

    socket.onmessage = (event) => {
      let message: Record<string, unknown>;
      try {
        message = JSON.parse(String(event.data));
      } catch {
        return;
      }
      this.handle(message);
    };

    socket.onerror = () => {
      // `onclose` always follows; reconnecting is handled there so it happens once.
    };

    socket.onclose = () => {
      this.socket = null;
      // A fresh connection is a fresh world; stale snapshots would render a ghost
      // arena for a moment after reconnecting.
      this.buffer = [];
      this.energy.clear();
      this.energyDirty = true;
      // A new match or connection restarts the server's tick counter, so the old
      // offset would place the render moment in the wrong era entirely.
      this.clockOffset = null;
      this.pendingAnchor = null;
      this.rateScale = 1;
      this.starvedSeen = 0;
      this.prediction.reset();
      if (this.stopped) {
        this.setStatus("closed");
        return;
      }
      this.setStatus("reconnecting");
      this.scheduleReconnect();
    };
  }

  private scheduleReconnect() {
    if (this.reconnectTimer) return;
    const delay = this.backoffMs;
    this.backoffMs = Math.min(this.options.maxBackoffMs, this.backoffMs * 2);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private handle(message: Record<string, unknown>) {
    switch (message.t) {
      case "joined": {
        const info = message as unknown as JoinedInfo;
        this.you = info.you;
        this.joined = info;
        if (info.tickHz > 0) this.serverMsPerTick = 1000 / info.tickHz;
        this.prediction.configure(info.settings ?? {});
        this.prediction.reset();
        this.setStatus("joined");
        this.options.onJoined?.(info);
        break;
      }
      case "snapshot": {
        this.applyEnergy(message);
        const receivedAt = this.options.now();
        const serverTime = (message.tick as number) * this.serverMsPerTick;
        this.clockOffset = updateClockOffset(this.clockOffset, receivedAt, serverTime);
        const snapshot = { ...(message as unknown as Snapshot), receivedAt, serverTime };
        this.buffer = pushSnapshot(this.buffer, snapshot);

        if (typeof message.qd === "number") {
          // Hold a frame or two buffered. Running dry costs a held tick, which is
          // visible as a hitch; running deep only costs a little input latency, so
          // the correction is deliberately lopsided toward staying fed.
          const depth = message.qd;
          const starved = typeof message.st === "number" && message.st > this.starvedSeen;
          if (starved) this.starvedSeen = message.st as number;
          const target = starved || depth < 1 ? 1.06 : depth > 4 ? 0.97 : 1;
          this.rateScale += (target - this.rateScale) * 0.2;
        }

        const ack = typeof message.ack === "number" ? message.ack : 0;
        const mine = this.you ? snapshot.players.find((player) => player.i === this.you) : undefined;
        if (mine) {
          // Without an arena yet the anchor is remembered, not dropped: otherwise the
          // prediction would never initialise and the local core would stay frozen.
          if (this.predictionWorld) this.prediction.reconcile(this.predictionWorld, mine, ack);
          else this.pendingAnchor = { player: mine, ack };
        }
        if (typeof message.ack === "number" && message.ack > 0) {
          const sentAt = this.sentAt.get(message.ack);
          if (sentAt !== undefined) {
            this.latencyMs = this.options.now() - sentAt;
            // Everything older than the acked frame can never be acked again.
            for (const key of this.sentAt.keys()) {
              if (key <= message.ack) this.sentAt.delete(key);
            }
          }
        }
        break;
      }
      case "pong": {
        const sentAt = this.pingSentAt.get(message.id as number);
        if (sentAt !== undefined) {
          this.pingMs = this.options.now() - sentAt;
          this.pingSentAt.delete(message.id as number);
        }
        break;
      }
      case "settings": {
        // An operator changed the balance mid-match; adopt it so the client draws
        // the same hitboxes and range the arena is now enforcing.
        const settings = message.settings as Partial<BalanceSettings> | undefined;
        if (settings) {
          if (this.joined) this.joined = { ...this.joined, settings: settings as BalanceSettings };
          this.prediction.configure(settings);
          this.options.onJoined?.(this.joined ?? ({ settings } as JoinedInfo));
        }
        break;
      }
      case "ranked":
        if (message.unranked) {
          this.options.onRanked?.({ unranked: true, daily: 0, weekly: null, allTime: null });
          break;
        }
        // Where the life that just ended placed. Computed by the arena from its own
        // score, so there is nothing here the client could have inflated.
        this.options.onRanked?.({
          daily: Number(message.daily),
          weekly: message.weekly == null ? null : Number(message.weekly),
          allTime: message.allTime == null ? null : Number(message.allTime),
        });
        break;
      case "events":
        if (Array.isArray(message.events)) this.options.onEvents?.(message.events);
        break;
      case "over":
        if (Array.isArray(message.board)) this.options.onMatchOver?.(message.board);
        break;
      default:
        break;
    }
  }

  private applyEnergy(message: Record<string, unknown>) {
    if (message.efull) {
      this.energy.clear();
      for (const item of (message.energy as NetEnergy[] | undefined) ?? []) this.energy.set(item.i, item);
      this.energyDirty = true;
      return;
    }
    const changed = message.ediff as NetEnergy[] | undefined;
    if (changed?.length) {
      for (const item of changed) this.energy.set(item.i, item);
      this.energyDirty = true;
    }
    const removed = message.edel as number[] | undefined;
    if (removed?.length) {
      for (const id of removed) this.energy.delete(id);
      this.energyDirty = true;
    }
  }

  /** Cached so a 60fps render loop does not rebuild the array every frame. */
  private energySnapshot(): NetEnergy[] {
    if (this.energyDirty) {
      this.energyList = [...this.energy.values()];
      this.energyDirty = false;
    }
    return this.energyList;
  }

  private readonly sentAt = new Map<number, number>();

  /** Records the latest intent. It is flushed on the next input tick. */
  setInput(input: PlayerInput) {
    this.input = input;
  }

  /**
   * Gives the prediction an arena to run its physics against — it needs the world
   * bounds and settings to clamp exactly the way the server does.
   */
  usePredictionWorld(game: GameData) {
    this.predictionWorld = game;
    if (this.pendingAnchor) {
      this.prediction.reconcile(game, this.pendingAnchor.player, this.pendingAnchor.ack);
      this.pendingAnchor = null;
    }
  }

  /**
   * One rendered frame: advance the predicted local core, then send intent if the
   * input interval has elapsed. Prediction runs every frame while input is sent at
   * `inputHz`, so the core moves at screen rate rather than in packet-sized steps.
   */
  tick(dtSeconds: number) {
    this.maybePing();
    if (!this.predictionWorld || !this.input || this.status !== "joined") return false;

    const step = this.serverMsPerTick / 1000;
    // Never simulate more than a quarter second in one frame: a backgrounded tab
    // would otherwise come back and replay minutes of input at once.
    this.stepAccumulator = Math.min(this.stepAccumulator + dtSeconds * this.rateScale, 0.25);

    let sent = false;
    while (this.stepAccumulator >= step) {
      this.seq += 1;
      // Predict and send the *same* step, so the server's reply can be replayed
      // exactly rather than approximately.
      this.prediction.advance(this.predictionWorld, this.input, step, this.seq);
      this.sendInput(this.seq);
      this.stepAccumulator -= step;
      sent = true;
    }
    this.partialStep = this.stepAccumulator;
    return sent;
  }

  /** One round trip per second is enough to keep an honest reading. */
  private maybePing() {
    if (!this.socket || this.status !== "joined") return;
    const now = this.options.now();
    if (now - this.lastPingAt < 1000) return;
    this.lastPingAt = now;
    const id = this.nextPingId++;
    this.pingSentAt.set(id, now);
    if (this.pingSentAt.size > 10) {
      const oldest = this.pingSentAt.keys().next().value;
      if (oldest !== undefined) this.pingSentAt.delete(oldest);
    }
    this.socket.send(JSON.stringify({ t: "ping", id }));
  }

  /**
   * Sends the stored intent if the input interval has elapsed. Call it every frame;
   * it rate-limits itself so the socket carries `inputHz` frames a second.
   *
   * Takes no timestamp on purpose. It used to accept one, and the render loop passed
   * a requestAnimationFrame timestamp while snapshots were stamped with Date.now() —
   * two different epochs, which silently disabled interpolation entirely. The client
   * owns its clock so those can never disagree again.
   */
  /** Puts one input frame on the wire. */
  private sendInput(seq: number) {
    if (!this.socket || !this.input) return;
    this.sentAt.set(seq, this.options.now());
    // Bound the map: a stalled server would otherwise let it grow forever.
    if (this.sentAt.size > 240) {
      const oldest = this.sentAt.keys().next().value;
      if (oldest !== undefined) this.sentAt.delete(oldest);
    }
    this.socket.send(JSON.stringify({
      t: "input",
      seq,
      mx: this.input.moveX,
      my: this.input.moveY,
      ax: this.input.aimX,
      ay: this.input.aimY,
      ai: this.input.aiming ? 1 : 0,
      at: this.input.attack ? 1 : 0,
      bo: this.input.boost ? 1 : 0,
      sh: this.input.shield ? 1 : 0,
    }));
  }

  /** The world to draw right now, or null while the buffer is still filling. */
  sample(): InterpolatedWorld | null {
    if (this.clockOffset === null) return null;
    const world = sampleWorld(this.buffer, this.options.now(), this.clockOffset);
    if (!world) return null;
    world.energy = this.energySnapshot();
    // Everyone else is drawn from the past; our own core is drawn from prediction.
    if (this.you && this.prediction.active) world.players = this.prediction.apply(world.players, this.you, this.partialStep);
    return world;
  }

  disconnect() {
    this.stopped = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.socket?.close();
    this.socket = null;
    this.buffer = [];
    this.setStatus("closed");
  }
}
